import React, { useEffect, useState } from 'react';

interface QuarterSelectionProps {
  years: string[];
  quarters: string[];
  selectedYear?: string;
  selectedQuarter?: string;
  onYearChange?: (year: string) => void;
  onQuarterChange?: (quarter: string) => void;
  /** When set, show a Country dropdown on the same row (admin RISP entry). */
  showCountry?: boolean;
  countries?: string[];
  selectedCountry?: string;
  onCountryChange?: (country: string) => void;
}

const QuarterSelection: React.FC<QuarterSelectionProps> = ({
  years,
  quarters,
  selectedYear: initialYear,
  selectedQuarter: initialQuarter,
  onYearChange,
  onQuarterChange,
  showCountry = false,
  countries = [],
  selectedCountry = '',
  onCountryChange,
}) => {
  const currentYear = new Date().getFullYear().toString();
  const currentQuarter = `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`;

  const [selectedYear, setSelectedYear] = useState<string>(initialYear || currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState<string>(initialQuarter || currentQuarter);

  useEffect(() => {
    if (initialYear) setSelectedYear(initialYear);
  }, [initialYear]);

  useEffect(() => {
    if (initialQuarter) setSelectedQuarter(initialQuarter);
  }, [initialQuarter]);

  useEffect(() => {
    // Initialize with default values only on component mount
    const initialize = () => {
      if (onYearChange) onYearChange(selectedYear);
      if (onQuarterChange) onQuarterChange(selectedQuarter);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedYear(value);
    if (onYearChange) onYearChange(value);
  };

  const handleQuarterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedQuarter(value);
    if (onQuarterChange) onQuarterChange(value);
  };

  const selectClass =
    'block min-w-[7rem] py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm';

  return (
    <div className="flex flex-wrap gap-4 items-center">
      <div className="flex gap-2.5 items-center">
        <label htmlFor="year" className="block text-sm font-medium text-gray-700 whitespace-nowrap">
          Year
        </label>
        <select
          id="year"
          className={selectClass}
          value={selectedYear}
          onChange={handleYearChange}
        >
          <option disabled value="">
            Select Year
          </option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-2.5 items-center">
        <label htmlFor="quarter" className="block text-sm font-medium text-gray-700 whitespace-nowrap">
          Quarter
        </label>
        <select
          id="quarter"
          className={selectClass}
          value={selectedQuarter}
          onChange={handleQuarterChange}
        >
          <option disabled value="">
            Select Quarter
          </option>
          {quarters.map((quarter) => (
            <option key={quarter} value={quarter}>
              {quarter}
            </option>
          ))}
        </select>
      </div>

      {showCountry && (
        <div className="flex gap-2.5 items-center">
          <label
            htmlFor="risp-country"
            className="block text-sm font-medium text-gray-700 whitespace-nowrap"
          >
            Country
          </label>
          <select
            id="risp-country"
            className={`${selectClass} min-w-[10rem]`}
            value={selectedCountry}
            onChange={(e) => onCountryChange?.(e.target.value)}
          >
            <option value="">Select country</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default QuarterSelection;
